import { cn } from '@foundation/utils/cn';

import {
  getResponsiveEntries,
  HeadlessCloseButton,
  HeadlessCloseButtonProps,
  resolveResponsiveProp,
  type Breakpoint,
  type Responsive,
} from '@design-system/headless-ui';
import { Close } from '@design-system/icons';

import { PRESS_SCALE } from '../internal/animations';

export type CloseButtonSize = 'small' | 'medium' | 'large';
export type CloseButtonVariant = 'ghost' | 'circle' | 'filledWhite';

export type CloseButtonProps = Omit<
  HeadlessCloseButtonProps,
  'icon' | 'className'
> & {
  readonly size?: Responsive<CloseButtonSize>;
  readonly variant?: CloseButtonVariant;
  readonly 'data-testid'?: string;
};

const SIZES = {
  base: { small: 'size-6', medium: 'size-8', large: 'size-10' },
  sm: { small: 'sm:size-6', medium: 'sm:size-8', large: 'sm:size-10' },
  md: { small: 'md:size-6', medium: 'md:size-8', large: 'md:size-10' },
  lg: { small: 'lg:size-6', medium: 'lg:size-8', large: 'lg:size-10' },
  xl: { small: 'xl:size-6', medium: 'xl:size-8', large: 'xl:size-10' },
} as const satisfies Record<Breakpoint, Record<CloseButtonSize, string>>;

const ICON_SIZES = {
  small: 12,
  medium: 16,
  large: 20,
} as const satisfies Record<CloseButtonSize, number>;

// Matches Figma's Close Button: Filled (circle), Ghost (transparent), FilledWhite (on dark/branded surfaces).
const VARIANTS = {
  circle: cn(
    'bg-pmui-bg-secondary text-pmui-fg-primary',
    'hover:bg-pmui-interactive-bg-default-hovered active:bg-pmui-interactive-bg-default-pressed',
    'disabled:bg-pmui-interactive-bg-disabled disabled:text-pmui-interactive-fg-disabled',
  ),
  ghost: cn(
    'bg-transparent text-pmui-fg-primary',
    'hover:bg-pmui-interactive-bg-default-hovered/70 active:bg-pmui-interactive-bg-default-pressed/50',
    'disabled:bg-pmui-interactive-bg-disabled/35 disabled:text-pmui-interactive-fg-disabled',
  ),
  filledWhite: cn(
    'bg-pmui-bg-primary text-pmui-fg-primary',
    'hover:bg-pmui-bg-secondary active:bg-pmui-bg-tertiary',
    'disabled:bg-pmui-interactive-bg-disabled disabled:text-pmui-interactive-fg-disabled',
  ),
} as const satisfies Record<CloseButtonVariant, string>;

function CloseButtonComponent({
  size,
  variant = 'ghost',
  'data-testid': dataTestId,
  ...buttonProps
}: CloseButtonProps) {
  const sizeConfig = resolveResponsiveProp(size, 'medium');

  return (
    <HeadlessCloseButton
      {...buttonProps}
      data-testid={dataTestId}
      icon={<Close size={ICON_SIZES[sizeConfig.base ?? 'medium']} />}
      className={cn(
        'rounded-full',
        PRESS_SCALE,
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-pmui-border-focus-ring-focus focus-visible:ring-offset-2',
        getResponsiveEntries(sizeConfig).map(([bp, s]) => SIZES[bp][s]),
        VARIANTS[variant],
      )}
    />
  );
}

export const CloseButton = CloseButtonComponent;
